import React from 'react';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { InfoTooltip } from './InfoTooltip';

// ─── SecondaryAxisPills ─────────────────────────────────────
// <purpose>THE rendering of the secondary classification axis — Grund-Code (gMSB/wMSB), Anliegen
// (VNB), Prozesstyp (WIM). One component so the convention cannot drift, ported here from the
// three-tier fallback that used to live inline in TicketRowMeta and let a vocabulary delivery
// diverge the face from the ruling below. Consumers today: TicketRowMeta, DashboardSeite (Verlauf
// hover tooltip + Details panel), TicketDetail, StatistikSeite (reason-code rows), TriagePanel
// (process-type chip — this migration also ended the one surface that used to render the FULL
// label with no overflow handling, because it was written separately from every other surface),
// and ChatbotTestSeite (per-message chip row + Letzte-Antwort panel — both were plain display, so
// they adopted this component rather than merely reusing its accessors, once inspection showed
// neither carried a click or active-state affordance). UploadsSeite still calls
// secondaryAxisText/secondaryAxisHover directly (not this component) for its own INTERACTIVE
// (removable, ×-bearing) reason-code chip, same boundary FilterPanel's secondary-axis filter
// bubble draws (own markup/active-state, resolution accessors only, see its own comment).</purpose>
// <dependencies>useCategoryMeta — secondaryAxisText / secondaryAxisHover are the only resolution
// source; this component decides presentation, never wording.</dependencies>
// <constraints>The ruling, fleet-wide with NO role exceptions: de-slugged all-lowercase face ·
// standard colouring (never the primary axis's identity colour — the typographic register is what
// tells a scanning clerk which axis they are looking at) · +1..+n overflow · full proper-case served
// label on hover, single pill included · NEVER abbreviated. Abbreviation belongs to the primary
// axis alone, on the colour strip. A surface with its own affordance (an interactive filter chip,
// e.g. UploadsSeite's removable reason-code filter) should reuse the resolution accessors
// (secondaryAxisText/secondaryAxisHover) rather than this component when it migrates — unifying an
// interactive chip with a display pill would be unification for its own sake.</constraints>
export type SecondaryPillVariant = 'row' | 'stats' | 'detail';

const BASE: React.CSSProperties = {
  fontWeight: 500, borderRadius: 10, color: theme.colors.navyMid,
  backgroundColor: theme.colors.bgLight, border: `1px solid ${theme.colors.borderLight}`,
  whiteSpace: 'nowrap', flexShrink: 0,
};

// Only the metrics differ per surface; colour and shape are fixed, because they are what carry the
// "this is the secondary axis" signal.
// stats' clamp (maxWidth/overflow/textOverflow/boxSizing) is real, not decorative — StatistikSeite
// sizes its reason-code lane to at most 180px (pillWidth caps there at a 24-char face; the longest
// baked gMSB face is exactly 24, so this bites only a served id past roughly 27 chars — reachable by
// VNB anliegen / wim process types) and ellipsizes what doesn't fit, title still carrying the full
// text. It composes cleanly with BASE's `whiteSpace: nowrap` — nowrap forces the text onto one line
// so `text-overflow: ellipsis` has a single line to truncate, rather than fighting it — so do not
// "simplify" one away thinking it's redundant with the other; they are two different jobs each
// clamp direction (BASE = never wrap, stats = clip at the lane's own width) that only compose
// correctly together.
const VARIANTS: Record<SecondaryPillVariant, React.CSSProperties> = {
  row:    { fontSize: 10, padding: '1px 8px' },
  stats:  {
    fontSize: 11, padding: '2px 8px', boxSizing: 'border-box',
    maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
    // display:'inline-block' blockifies this span so maxWidth/overflow/textOverflow can apply at
    // all — a plain inline element ignores them outright. The clamp works today without this
    // because the one 'stats' call site (StatistikSeite) happens to render it as a flex child,
    // which blockifies it incidentally — but that is a fact about the caller, not about 'stats'.
    // The predecessor style object this variant replaced (rcPillStyle) carried this explicitly for
    // exactly that reason, and its removal in an earlier collapse silently made the clamp depend on
    // callers never changing — restoring it here makes 'stats' self-contained again. Do NOT delete
    // as "redundant with the flex parent": the parent is incidental, this is not.
    display: 'inline-block',
  },
  detail: { fontSize: 11, padding: '2px 10px' },
};

// neutralPillStyle — THE style factory behind every plain (non-interactive, non-primary-axis) pill
// in the app: this component's own secondary-axis pill, and the row's other neutral chips (pending
// chip, statusKurz, ClerkInstructionPanel's "Fehlt:" chip) that share the identical bg/border/shape
// but carry no secondary-axis meaning at all. Both used to hand-roll their own copy of this exact
// object — one factory now, so a future colour/shape change can't drift between the copies the way
// the deleted rcPillStyle already proved it could (see the 'stats' comment above).
export const neutralPillStyle = (variant: SecondaryPillVariant): React.CSSProperties =>
  ({ ...BASE, ...VARIANTS[variant] });

export const SecondaryAxisPills: React.FC<{
  codes: string[];
  maxVisible: number;
  variant?: SecondaryPillVariant;
}> = ({ codes, maxVisible, variant = 'row' }) => {
  const cm = useCategoryMeta();
  if (codes.length === 0) return null;
  const style = neutralPillStyle(variant);
  const hidden = codes.slice(maxVisible);
  return (
    <>
      {codes.slice(0, maxVisible).map(code => (
        <InfoTooltip key={code} text={cm.secondaryAxisHover(code)}>
          <span style={style}>
            {cm.secondaryAxisText(code)}
          </span>
        </InfoTooltip>
      ))}
      {hidden.length > 0 && (
        <InfoTooltip text={hidden.map(cm.secondaryAxisHover).join(', ')}>
          <span style={style}>
            +{hidden.length}
          </span>
        </InfoTooltip>
      )}
    </>
  );
};
